import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import SEO from '../components/SEO';
import AppShell from '../components/AppShell';
import { SummarosMark } from '../components/Logo';

// Hero decoration. On mobile and tablet it sits inline BELOW the
// text so it never overlaps. At lg+ it floats absolutely to the
// right of the hero copy. The illustration is a person quietly
// looking out at a landscape — the visual anchor for
// "understand your whole life".
const HeroArt = () => (
  <div
    className="
      mt-14 flex justify-center
      lg:mt-0 lg:absolute lg:inset-0 lg:pointer-events-none
      lg:flex lg:items-center lg:justify-end lg:pr-8 xl:pr-16
    "
    aria-hidden="true"
    data-testid="hero-orbit"
  >
    <img
      src="/art/hero.png"
      alt=""
      draggable={false}
      className="
        select-none w-[min(280px,80vw)] h-auto
        sm:w-[min(380px,60vw)]
        lg:w-[460px]
      "
    />
  </div>
);

const Home = () => {
  return (
    <AppShell>
      <SEO
        title="Understand your whole life"
        description="Health. Work. Money. Habits. Relationships. Goals. It all connects. Summaros helps you see the bigger picture."
        path="/"
      />

      {/* ── Hero ───────────────────────────────────────── */}
      <section className="relative overflow-hidden grain">
        <div className="relative max-w-6xl mx-auto px-6 sm:px-8 pt-20 sm:pt-28 pb-24 sm:pb-36">
          <div className="max-w-2xl animate-fade-up relative z-10">
            <p className="eyebrow mb-6" data-testid="hero-eyebrow">
              Life Intelligence
            </p>
            <h1
              className="display-hero text-[46px] sm:text-[68px] lg:text-[84px] leading-[1.02] mb-8 text-balance"
              data-testid="hero-title"
              style={{ fontVariationSettings: "'opsz' 144, 'SOFT' 60, 'wght' 340" }}
            >
              Understand your whole life.
            </h1>
            <p
              className="text-[19px] sm:text-[22px] leading-[1.55] text-ink-muted mb-10 text-pretty max-w-xl"
              data-testid="hero-sub"
            >
              Health. Work. Money. Habits. Relationships. Goals.
              <br className="hidden sm:block" />
              It all connects.
            </p>
            <div className="flex flex-wrap items-center gap-3" data-testid="hero-cta-row">
              <Link to="/login?next=/welcome" className="btn-primary" data-testid="cta-start">
                Start with me <ArrowRight className="w-4 h-4" strokeWidth={2} />
              </Link>
              <Link to="/how" className="btn-secondary" data-testid="cta-how">
                See how it works
              </Link>
            </div>
          </div>
          <HeroArt />
        </div>
      </section>

      {/* ── The connection idea ────────────────────────── */}
      <section className="bg-cream-soft border-y border-line">
        <div className="max-w-5xl mx-auto px-6 sm:px-8 py-24 sm:py-32">
          <div className="grid md:grid-cols-12 gap-10 md:gap-16 items-start">
            <div className="md:col-span-5">
              <p className="eyebrow mb-5">The idea</p>
              <h2
                className="display-serif text-4xl sm:text-5xl leading-[1.08] text-forest"
                data-testid="idea-title"
                style={{ fontVariationSettings: "'opsz' 96, 'SOFT' 50, 'wght' 380" }}
              >
                Life isn't one score.
              </h2>
            </div>
            <div className="md:col-span-7 space-y-5 text-[17px] sm:text-[19px] leading-[1.6] text-ink" data-testid="idea-body">
              <p>Sleep affects work.</p>
              <p>Money affects stress.</p>
              <p>Relationships affect health.</p>
              <p>Habits affect almost everything.</p>
              <p className="text-ink-muted pt-4 border-t border-line">
                Summaros is a quiet way to look at the parts of your life together, and notice how they shape each other.
              </p>
              <div className="pt-4">
                <Link to="/login?next=/welcome" className="btn-primary" data-testid="cta-build-profile">
                  Build my Life Profile <ArrowRight className="w-4 h-4" strokeWidth={2} />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── What you'll do ─────────────────────────────── */}
      <section className="max-w-6xl mx-auto px-6 sm:px-8 py-24 sm:py-32">
        <div className="mb-14">
          <p className="eyebrow mb-4">What you'll do</p>
          <h2 className="display-serif text-3xl sm:text-4xl text-forest max-w-2xl">
            A quiet, curious look at where you are.
          </h2>
        </div>

        <div className="grid md:grid-cols-3 gap-5" data-testid="steps-grid">
          {[
            {
              step: '01',
              title: 'Life Assessment',
              body: 'A short chat across a few parts of your life. Body, mind, work, money, relationships, habits, and a few more. No 1 to 10 scales, just real answers.',
            },
            {
              step: '02',
              title: 'Life Profile',
              body: "Not a score, more like a sketch. What's working, what's worth watching, and how things quietly connect.",
            },
            {
              step: '03',
              title: 'My Life',
              body: 'A private space of your own. Notice change. Keep track of what you care about. Add life events as they happen.',
            },
          ].map((s) => (
            <div
              key={s.step}
              className="card hover:border-forest/40 hover:-translate-y-0.5"
              data-testid={`step-${s.step}`}
            >
              <p className="font-mono text-xs text-clay tracking-widest mb-4">{s.step}</p>
              <h3 className="font-display text-2xl text-forest mb-3" style={{ fontVariationSettings: "'opsz' 48, 'SOFT' 40, 'wght' 420" }}>
                {s.title}
              </h3>
              <p className="text-[15px] leading-relaxed text-ink-muted">{s.body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ── Private / shared line ─────────────────────── */}
      <section className="bg-forest text-cream">
        <div className="max-w-5xl mx-auto px-6 sm:px-8 py-20 sm:py-28 text-center">
          <div className="inline-flex items-center justify-center mb-8 opacity-80">
            <SummarosMark size={22} />
          </div>
          <h2
            className="display-hero text-3xl sm:text-5xl leading-[1.1] text-cream mb-6"
            style={{ fontVariationSettings: "'opsz' 144, 'SOFT' 60, 'wght' 320" }}
          >
            Private life. Shared ideas.
          </h2>
          <p className="text-[17px] sm:text-[19px] leading-[1.6] text-cream/75 max-w-xl mx-auto">
            Summaros understands you privately.
            Community is a place to talk about being human in general.
            The two are kept separate on purpose.
          </p>
        </div>
      </section>
    </AppShell>
  );
};

export default Home;
